import type { Attributes } from '@tiptap/core';
import { isObject } from 'lodash';
import type { DOMOutputSpec } from 'ricos-types';
import { firstRight } from 'wix-rich-content-common';
import { createRicosNodeExtension } from 'wix-tiptap-extensions';

import { UnsupportedBlock as Component } from './unsupported-node/component';

const getDefaultValue = (value: unknown) =>
  firstRight(value, value, [
    [v => typeof v === 'number', () => 0],
    [v => typeof v === 'string', () => ''],
    [v => typeof v === 'boolean', () => false],
    [v => Array.isArray(v), () => []],
    [v => isObject(v), () => null],
  ]);

const toGlobalAttributes = (attrs: Record<string, unknown>): Attributes =>
  Object.keys(attrs).reduce(
    (globalAttributes, key) => ({
      ...globalAttributes,
      [key]: { default: getDefaultValue(attrs[key]) },
    }),
    {}
  );

/**
 * Creates RicosExtension for unsupported node, dynamically
 */
export const getUnsupportedNodeConfig = ({ unsupportedNodeType, ...attrs }) => {
  return createRicosNodeExtension<{
    HTMLAttributes: Record<string, unknown>;
  }>({
    groups: ['draggable'],
    type: 'react-node',
    Component,
    name: unsupportedNodeType,
    createExtensionConfig: ({ mergeAttributes }) => ({
      name: unsupportedNodeType,
      addOptions() {
        return {
          HTMLAttributes: {},
        };
      },

      addGlobalAttributes: () => [
        {
          types: [unsupportedNodeType],
          attributes: toGlobalAttributes(attrs),
        },
      ],
      content: 'block*',
      group: 'block',
      selectable: true,
      draggable: true,
      marks: '_',
      atom: true,
      allowGapCursor: false,

      parseHTML() {
        return [{ tag: unsupportedNodeType }];
      },

      renderHTML({ HTMLAttributes }) {
        return [
          unsupportedNodeType,
          mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
          0,
        ] as DOMOutputSpec;
      },
    }),
  });
};
